import type { CheckoutAttemptResult } from '../../lib/checkoutLifecycleContract';
import type { CheckoutResultMode } from '../../lib/checkoutResultContract';
import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';

export type CheckoutSyncState =
    | 'checking'
    | 'ready'
    | 'pending'
    | 'signed_out'
    | 'error'
    | 'left'
    | 'closed';

export type CheckoutCopy = {
    eyebrow: MessageDescriptor;
    title: MessageDescriptor;
    body: MessageDescriptor;
    finePrint?: MessageDescriptor;
};

function formatPlan(lookupKey?: string | null): string {
    const planId = lookupKey?.split('_')[0];
    return planId ? planId.charAt(0).toUpperCase() + planId.slice(1) : 'Starter';
}

export function checkoutStatusCopy(
    state: CheckoutSyncState,
    mode: CheckoutResultMode,
    result: CheckoutAttemptResult | null,
    hasPreviewProject: boolean,
    hasLegacySession: boolean,
): CheckoutCopy {
    if (state === 'ready' && result) {
        return {
            eyebrow: result.trialStatus === 'trialing'
                ? msg({ id: 'checkout.result.trialActivated', message: 'Trial activated' })
                : msg({ id: 'checkout.result.subscriptionActive', message: 'Subscription active' }),
            title: {
                ...msg({ id: 'checkout.result.planReady', message: '{plan} is ready' }),
                values: { plan: formatPlan(result.planLookupKey) },
            },
            body: result.trialStatus === 'trialing'
                ? msg({ id: 'checkout.result.trialActiveBody', message: 'Stripe confirmed this exact checkout and your Paperzilla trial is active.' })
                : msg({ id: 'checkout.result.subscriptionActiveBody', message: 'Stripe confirmed this exact checkout and your Paperzilla subscription is active.' }),
            finePrint: result.trialStatus === 'trialing'
                ? msg({
                    id: 'checkout.result.authoritativeTrialTerms',
                    message: 'Your receipt contains the authoritative trial dates and renewal terms.',
                })
                : undefined,
        };
    }

    if (result?.completed) {
        return {
            eyebrow: msg({ id: 'checkout.result.completed', message: 'Checkout completed' }),
            title: msg({ id: 'checkout.result.finishingAccess', message: 'Finishing your access' }),
            body: msg({ id: 'checkout.result.syncingAccess', message: 'Stripe confirmed this exact checkout. Paperzilla is still syncing the subscription and project access.' }),
        };
    }

    if (state === 'signed_out') {
        return {
            eyebrow: msg({ id: 'checkout.result.returnReceived', message: 'Checkout return received' }),
            title: hasLegacySession
                ? msg({ id: 'checkout.result.signInLegacy', message: 'Sign in to check this checkout' })
                : msg({ id: 'checkout.result.signInExact', message: 'Sign in to check the exact checkout' }),
            body: hasLegacySession
                ? msg({ id: 'checkout.result.signInLegacyBody', message: 'Sign in with the same Paperzilla account. We will use the Stripe return reference to find and verify the checkout that belongs to you.' })
                : msg({ id: 'checkout.result.signInExactBody', message: 'Sign in with the same Paperzilla account so we can verify the exact checkout attempt and subscription state.' }),
        };
    }

    if (mode === 'cancelled') {
        return {
            eyebrow: msg({ id: 'checkout.result.left', message: 'Checkout left' }),
            title: msg({ id: 'checkout.result.leftTitle', message: 'You left secure checkout' }),
            body: hasPreviewProject
                ? msg({ id: 'checkout.result.leftPreviewBody', message: 'This page does not cancel a trial or subscription. If you did not finish in Stripe, tracking remains paused and you can continue the same checkout when ready. If you did finish, Paperzilla is still checking the exact checkout status.' })
                : msg({ id: 'checkout.result.leftBody', message: 'This page does not cancel a trial or subscription. You can continue the same checkout when ready. If you finished in Stripe, Paperzilla is still checking the exact checkout status.' }),
        };
    }

    if (state === 'closed' && result) {
        const wasSuperseded = result.state === 'superseded';
        return {
            eyebrow: msg({ id: 'checkout.result.notCompleted', message: 'Checkout not completed' }),
            title: wasSuperseded
                ? msg({ id: 'checkout.result.superseded', message: 'A newer checkout replaced this one' })
                : msg({ id: 'checkout.result.expired', message: 'This checkout expired' }),
            body: wasSuperseded
                ? msg({ id: 'checkout.result.supersededBody', message: 'Paperzilla did not open access from this checkout. Review the current trial terms before continuing the newer checkout.' })
                : msg({ id: 'checkout.result.expiredBody', message: 'Paperzilla did not open access from this checkout. Review the latest trial terms to start a fresh secure checkout.' }),
        };
    }

    if (state === 'pending') {
        return {
            eyebrow: msg({ id: 'checkout.result.pending', message: 'Checkout status pending' }),
            title: msg({ id: 'checkout.result.stillChecking', message: 'Still checking this checkout' }),
            body: msg({ id: 'checkout.result.pendingBody', message: 'Paperzilla has not yet received enough authoritative Stripe state to open paid access. Do not start a second checkout while this one is syncing.' }),
        };
    }

    if (state === 'error') {
        return {
            eyebrow: msg({ id: 'checkout.result.unavailable', message: 'Checkout status unavailable' }),
            title: msg({ id: 'checkout.result.verifyFailure', message: 'We could not verify this checkout yet' }),
            body: msg({ id: 'checkout.result.verifyFailureBody', message: 'Paperzilla could not read the exact checkout attempt. Try checking again before starting another checkout.' }),
        };
    }

    return {
        eyebrow: msg({ id: 'checkout.result.checking', message: 'Checking checkout' }),
        title: msg({ id: 'checkout.result.confirming', message: 'Confirming this exact checkout' }),
        body: msg({ id: 'checkout.result.confirmingBody', message: 'Paperzilla is waiting for the authoritative checkout and subscription state from Stripe.' }),
    };
}
